import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCompanyPolicies, getCompanyPolicy } from "../../api/companyPolicies";
import AttachmentList from "./AttachmentList";
import { useNotifications } from "../../context/NotificationsContext";
import {
  getCompanyPolicyNotificationIds,
  getUnreadCompanyPolicyIds,
} from "../../utils/notificationTarget";
import "./CompanyPoliciesInbox.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

function audienceLabel(item) {
  const target = item?.target || "ALL";
  if (target === "DEPARTMENT") {
    const names = (item.departments || [])
      .map((d) => d.name)
      .filter(Boolean);
    if (names.length === 1) return `لائحة ${names[0]}`;
    if (names.length) return names.join("، ");
    return "لائحة الإدارة";
  }
  if (target === "EMPLOYEES") return "لائحة موجهة لك";
  if (target === "JOB_TITLE") {
    const titles = (item.jobTitles || []).filter(Boolean);
    if (titles.length === 1) return `لائحة ${titles[0]}`;
    if (titles.length) return titles.join("، ");
    return "لائحة المسمى الوظيفي";
  }
  return "لائحة عامة";
}

function CompanyPoliciesInbox({ initialPolicyId = null }) {
  const { notifications, markAsRead } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(initialPolicyId);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const unreadIds = useMemo(
    () => getUnreadCompanyPolicyIds(notifications),
    [notifications],
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getCompanyPolicies({ status: "PUBLISHED" });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل لائحة الشركة"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (initialPolicyId) {
      setSelectedId(initialPolicyId);
    }
  }, [initialPolicyId]);

  useEffect(() => {
    if (!selectedId) return;
    const ids = getCompanyPolicyNotificationIds(notifications, selectedId);
    ids.forEach((id) => {
      const notif = notifications.find((n) => String(n.id) === String(id));
      if (notif && !(notif.isRead ?? notif.read)) {
        markAsRead(id).catch(() => {});
      }
    });
  }, [selectedId, notifications, markAsRead]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }

    let cancelled = false;
    const fromList = items.find((item) => item.id === selectedId);
    if (fromList?.body) {
      setSelected(fromList);
    }

    setDetailLoading(true);
    getCompanyPolicy(selectedId)
      .then((data) => {
        if (!cancelled) setSelected(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, "تعذر فتح اللائحة"));
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedId, items]);

  return (
    <div className="company-policies-inbox" dir="rtl">
      <div className="company-policies-inbox-header">
        <h1 className="company-policies-inbox-title">لائحة الشركة</h1>
        <p className="company-policies-inbox-subtitle">
          اللوائح الصادرة من الموارد البشرية لشركتك أو لإدارتك أو الموجهة لك مباشرة.
        </p>
      </div>

      {error ? (
        <div className="company-policies-inbox-alert">{error}</div>
      ) : null}

      {selectedId ? (
        <div className="company-policies-inbox-detail">
          <button
            type="button"
            className="company-policies-inbox-back"
            onClick={() => setSelectedId(null)}
          >
            رجوع للقائمة
          </button>
          {detailLoading && !selected ? (
            <div className="company-policies-inbox-empty">جاري التحميل...</div>
          ) : selected ? (
            <article className="company-policies-inbox-card is-detail">
              <div className="company-policies-inbox-card-top">
                <h2>{selected.title}</h2>
              </div>
              <div className="company-policies-inbox-meta">
                {formatDateTime(selected.publishedAt || selected.updatedAt)}
                <span className="company-policies-inbox-clip">
                  {audienceLabel(selected)}
                </span>
              </div>
              {selected.summary ? (
                <p className="company-policies-inbox-summary">{selected.summary}</p>
              ) : null}
              {selected.body ? (
                <p className="company-policies-inbox-body">{selected.body}</p>
              ) : null}
              <AttachmentList
                entityType="company_policy"
                entityId={selected.id}
                showDelete={false}
                hideIfEmpty
              />
            </article>
          ) : null}
        </div>
      ) : loading ? (
        <div className="company-policies-inbox-empty">جاري التحميل...</div>
      ) : items.length === 0 ? (
        <div className="company-policies-inbox-empty">
          لا توجد لوائح متاحة لك حالياً
        </div>
      ) : (
        <div className="company-policies-inbox-list">
          {items.map((item) => {
            const isUnread = unreadIds.has(String(item.id));
            return (
              <button
                key={item.id}
                type="button"
                className={`company-policies-inbox-card${isUnread ? " is-unread" : ""}`}
                onClick={() => setSelectedId(item.id)}
              >
                <div className="company-policies-inbox-card-top">
                  <h2>
                    {item.title}
                    {isUnread ? (
                      <span className="company-policies-inbox-new">جديد</span>
                    ) : null}
                  </h2>
                </div>
                <div className="company-policies-inbox-meta">
                  {formatDateTime(item.publishedAt || item.updatedAt)}
                  <span className="company-policies-inbox-clip">
                    {audienceLabel(item)}
                  </span>
                  {item.attachmentCount > 0 ? (
                    <span className="company-policies-inbox-clip">مرفق</span>
                  ) : null}
                </div>
                <p className="company-policies-inbox-preview">
                  {item.summary || item.body || "اضغط لفتح ملف اللائحة"}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CompanyPoliciesInbox;
